import { createContext, type ReactNode, useContext, useId, useMemo } from 'react';
import { useFieldError } from './use-field-error.js';

interface FieldA11y {
  /** id of the control itself — what the label's htmlFor points at. */
  controlId: string;
  /** id of the error paragraph, present only while there is an error. */
  errorId: string;
  /** id of the hint paragraph, present only when a hint was supplied. */
  hintId: string;
  hasError: boolean;
  hasHint: boolean;
}

const FieldContext = createContext<FieldA11y | null>(null);

export function FieldProvider({
  name,
  hasHint,
  children,
}: {
  name: string;
  hasHint: boolean;
  children: ReactNode;
}) {
  // useId keeps two instances of the same field name (a repeater row rendered twice,
  // a field reused in a dialog) from colliding on DOM ids.
  const uid = useId();
  const error = useFieldError(name);

  const value = useMemo<FieldA11y>(
    () => ({
      controlId: `${name}-${uid}`,
      errorId: `${name}-${uid}-error`,
      hintId: `${name}-${uid}-hint`,
      hasError: !!error,
      hasHint,
    }),
    [name, uid, error, hasHint],
  );

  return <FieldContext.Provider value={value}>{children}</FieldContext.Provider>;
}

/**
 * A11y wiring for a control. Returns the props every field must spread onto its input so
 * screen readers get the label, the hint and the error — none of which were connected
 * before: fields set `id={name}` and nothing set `aria-invalid` or `aria-describedby`.
 *
 * Falls back to bare `name` when a field is rendered outside `FormField`, so a caller
 * composing controls by hand still gets a usable id instead of an exception.
 */
export function useFieldA11y(name: string): {
  id: string;
  'aria-invalid': boolean;
  'aria-describedby': string | undefined;
} {
  const ctx = useContext(FieldContext);
  const fallbackError = useFieldError(name);

  if (!ctx) {
    return { id: name, 'aria-invalid': !!fallbackError, 'aria-describedby': undefined };
  }

  const describedBy = [ctx.hasError && ctx.errorId, ctx.hasHint && !ctx.hasError && ctx.hintId]
    .filter(Boolean)
    .join(' ');

  return {
    id: ctx.controlId,
    'aria-invalid': ctx.hasError,
    'aria-describedby': describedBy || undefined,
  };
}

/** Ids for `FormField` itself to stamp on its label, hint and error nodes. */
export function useFieldIds(): FieldA11y | null {
  return useContext(FieldContext);
}
